import { useEffect, useState } from "react";
import moment from "moment";
import {
  copyMessage, getVolatilityClickUsers, getVolatilityMessage,
} from "../api/message/volatility-message.api";
import {useSearchParams} from "react-router-dom";
import MessageWrapPreview from "./volatility-message/content/preview/MessageWrapPreview";
import {
  Button, Chip,
  CoinIcon, CouponIcon,
  MouseIcon,
  Pagination,
  Panel, Tab, Table, UsersIcon
} from "omoplay";
import {basicBarOption} from "../constants/chart_config";
import CommonCalendarSearchBar from "./CommonCalendarSearchBar";
import {getMessageOrdersStatistics} from "../api/statistics/statistics.api";
import InputGroup from "./form/InputGroup";
import MiniAIChatForm from "./MiniAIChatForm";
import Markdown from "react-markdown";
import EChartsReact from "echarts-for-react";
import { ReactComponent as ArrowLeft } from "../assets/images/arrrow-left.svg";
import { flattenFilterLabels } from "../libs/filterLabels";

const VolatilityMessageDetail = () => {
  const [query] = useSearchParams();
  const id = query.get("id")
  const [tab, setTab] = useState("init")
  const [message, setMessage] = useState({})
  const [previewData, setPreviewData] = useState({})
  /** 발송 조건 chip 에 찍을 **문구 목록**(문자열 배열). @see flattenFilterLabels */
  const [filterCategories, setFilterCategories] = useState([])
  const [clickUserData, setClickUserData] = useState({})
  const [contentsClickUserTable, setContentsClickUserTable] = useState([])
  const [totalClickUserTable, setTotalClickUserTable] = useState([])
  const [clickUserChart, setClickUserChart] = useState({})
  const [dateRange, setDateRange] = useState({})
  const [orderTables, setOrderTables] = useState([])
  const [orderTotalCount, setOrderTotalCount] = useState(0)
  const [orderTotalSales, setOrderTotalSales] = useState(0)
  const [orderPage, setOrderPage] = useState(1)
  // 자사몰 주문 조회(statistics-parsing-service)가 실패했을 때의 사유.
  // 🔴 실패를 '0원' 으로 보여주면 장애가 '매출 0원 / ROAS 0%' 으로 읽힌다(2026-09-11, D-8).
  const [orderError, setOrderError] = useState("")
  const [clickRemarketingUsers, setClickRemarketingUsers] = useState([])
  const [clickUserCount, setClickUserCount] = useState(0)

  const orderLimit = 10

  const tabHeaders = [
    {label: "개요", value: "init", selected: true}, {label: "AI 어시스턴트", value: "ai"},
    {label: "인게이지먼트", value: "engage"}, {label: "매출", value: "sales"}, /*{label: "리마인드", value: "sales_marketing"}*/
  ]
  const sentHeaders = [
      {name: "메시지 발송 구분"}, {name: "발송 시도 수"}, {name: "발송 성공 수"}, {name: "총 클릭 수"}
  ]

  const contentsClickHeaders = [
      {name: "위치", width: "90px"}, {name: "클릭 컨텐츠"}, {name: "형태", width: "90px"}, {name: "클릭", width: "160px"}
  ]

  const clickUserHeaders = [
    { name: "이름", align: "center", width: "100px" },
    { name: "클릭 컨텐츠" },
  ];

  const orderHeaders = [
    { name: "상품명", align: "center" },
    { name: "채널", width: "120px", align: "center" },
    { name: "이름", align: "center", width: "100px" },
    { name: "성별", align: "center", width: "70px" },
    { name: "연령", align: "center", width: "110px" },
    { name: "주문 상태", width: "110px", align: "center" },
    { name: "결제 가격", width: "120px", align: "center" },
    { name: "결제 날짜", width: "120px", align: "center" },
  ];

  const displayGenderText = (gender) => {
    if (gender === "male") {
      return "남성";
    } else if (gender === "female") {
      return "여성";
    } else {
      return gender;
    }
  }

  useEffect(() => {
    // 개인(고객) 클릭 수 + 모먼트분 추정치(개인 클릭률 × 모먼트 발송성공수). 발송성공 0이면 추정치 0으로(0나눗셈 방지).
    const personalClicks = clickUserData.totalClickUsers?.length ?? 0
    const momentEstimate = message.totalSuccessSendCount > 0
        ? Math.floor(personalClicks / message.totalSuccessSendCount * (message.momentSuccessSendCount ?? 0))
        : 0
    setClickUserCount(personalClicks + momentEstimate)
  }, [message, clickUserData])

  useEffect(() => {
    getVolatilityMessage(id).then(({data}) => {
      setMessage(data)
      setFilterCategories(flattenFilterLabels(data.filterLabels ?? data.filters))
      setPreviewData(data.messageData)
    })

    getVolatilityClickUsers(id, dateRange.startDate, dateRange.endDate).then(({data}) => {
      if (data) {
        setClickUserData(data)
        setContentsClickUserTable(data.contents.map(content => {
          return [
              <div className="flex-align-center h-full">{content.indexName}</div>,
              <div>
                {
                  content.typeName === "버튼"
                      ? <Button variants="grey" value={content.name} />
                      : (content.typeName === "이미지" || content.typeName === "아이템"
                          ? <div className="flex-align-center gap-2">
                            <img alt={content.name} src={content.imgUrl} width={128} height={128} />
                            {content.name}
                      </div> : (content.typeName === "쿠폰" ? content.name : <></>) )
                }
              </div>,
              <div className="flex-align-center h-full">{content.typeName}</div>,
              <div className="flex-align-center h-full">{content.totalCount.toLocaleString()}회</div>
          ]
        }))
        setTotalClickUserTable(data.totalClickUsers.map(user => {
          return [
            <div className={`flex-center-all h-full break-all ${user.id && 'text-primary pointer'}`}
                 onClick={() => {if (user.id) userDetail(user.id)}}>{user.name}</div>,
            <div className="flex-align-center h-full gap-4">{user.clickContents.map(content => {
              return (
                  <div className="flex-align-center gap-2">
                    <div className="w-[150px]">
                      <div className="font-semibold text-center w-full">{content.indexName} (클릭 {content.totalCount}번)</div>
                      {
                        content.typeName === "버튼"
                            ? <div className="text-center"><Button variants="grey" value={content.name} /></div>
                            : (content.typeName === "이미지" || content.typeName === "아이템"
                                ? (
                                    <div>
                                      <img alt={content.name} src={content.imgUrl} width={150} height={75} />
                                    </div>
                                ) : (content.typeName === "쿠폰" ? <div className="text-center">{content.name}</div> : <></>) )
                      }
                    </div>
                    <div className="flex-col gap-2">
                      {
                        content.clickDate.map(date => <div>{date}</div>)
                      }
                    </div>
                  </div>
              )
            })}</div>,
          ]
        }))

        setClickRemarketingUsers(
            data.totalClickUsers
                .filter(user => user.clickContents.filter(content => content.totalCount > 1).length > 0)
                .map(user => {
                  return [
                    <div className={`flex-center-all h-full break-all ${user.id && 'text-primary pointer'}`}
                         onClick={() => {if (user.id) userDetail(user.id)}}>{user.name}</div>,
                    <div className="flex-center-all h-full">{displayGenderText(user.gender)}</div>,
                    <div className="flex-center-all h-full">{user.age}</div>,
                    <div className="flex-align-center h-full gap-4">{user.clickContents.map(content => {
                      return (
                          <div className="flex-align-center gap-2">
                            <div className="w-[150px]">
                              <div className="font-semibold text-center w-full">{content.indexName} (클릭 {content.totalCount}번)</div>
                              {
                                content.typeName === "버튼"
                                    ? <div className="text-center"><Button variants="grey" value={content.name} /></div>
                                    : (content.typeName === "이미지" || content.typeName === "아이템"
                                        ? (
                                            <div>
                                              <img alt={content.name} src={content.imgUrl} width={150} height={75} />
                                            </div>
                                        ) : <></>)
                              }
                            </div>
                          </div>
                      )
                    })}</div>,
                  ]
                })
        )

        const xAxis = []
        const series = []

        Object.entries(data.groupDateClickCount).forEach(([key, value]) => {
          xAxis.push(key)
          series.push(value)
        })
        setClickUserChart({
          xAxis: xAxis,
          series: series
        });
      }
    })
  }, [id, dateRange]);

  function productDetail(productCode) {
    window.location.href = `/product/detail?productCode=${productCode}`
  }

  useEffect(() => {
    setOrderError("")
    getMessageOrdersStatistics(id, orderPage, orderLimit).then(({data}) => {
      if (data) {
        setOrderTotalCount(data.totalCount)
        setOrderTotalSales(data.totalSales)
        setOrderTables(data.users.map(user => {
          return [
              <div>
                {
                  user.products.map(({imgUrl, code, name}) => {
                    if (!imgUrl) {
                      return <div className="ml-[72px]">{name}</div>
                    }
                    return (
                        <div className="flex-align-center gap-2">
                          <img src={imgUrl} className="w-16 h-16" />
                          <div className="text-primary pointer" onClick={() => productDetail(code) }>{name}</div>
                        </div>
                    )
                  })
                }
              </div>,
              <div className="flex-center-all h-full">{user.channel ?? "자사몰(온라인)"}</div>,
            <div className={`flex-center-all h-full break-all ${user.id && 'text-primary pointer'}`}
                 onClick={() => {if (user.id) userDetail(user.id)}}>{user.name}</div>,
              <div className="flex-center-all h-full">{user.gender}</div>,
              <div className="flex-center-all h-full">{user.age}</div>,
              <div className="flex-center-all h-full">{user.productStatus}</div>,
              <div className="flex-center-all h-full">{user.paymentAmount.toLocaleString()}원</div>,
              <div className="flex-center-all h-full">{user.paymentDate}</div>,
          ]
        }))
      }
    }).catch((e) => {
      // 🔴 catch 가 없으면 처리되지 않은 rejection 이 된다 — fetch.js 의 503 분기가 reject 로
      //    돌려주기 때문이다(같은 변경에서 함께 넣어야 하는 짝, 2026-09-11 D-8).
      setOrderTotalCount(0)
      setOrderTotalSales(0)
      setOrderTables([])
      setOrderError(e?.message || "주문 정보를 불러오지 못했습니다.")
    })
  }, [id, orderPage]);

  function userDetail(id) {
    window.location.href = `/user/detail?id=${id}`
  }

  const displayStatus = (status, errorCode) => {
    if (status === "2") {
      return <span>발송 성공</span>
    } else if (status === "4") {
      return <><span style={{color: "#CE326C"}}>발송 실패</span><br/><span>({errorCode})</span></>
    } else {
      return <span>결과 대기</span>;
    }
  };

  return (
      <div>
        <div className="flex justify-between items-center mb-2">
          <div className="flex gap-4">
            <div className="w-10 h-10">
              <Button variants="outlined"
                      onClick={() => window.location.href = ("/volatility-message")}
                      value={<ArrowLeft />}
              />
            </div>
            <h4 className="text-3xl font-semibold">{message.campaignName}</h4>
          </div>
          <div>
            <Button variants="dark" value="복제하기" onClick={() => {
              if (window.confirm("정말 복제하시겠습니까?")) {
                copyMessage(id).then(() => {
                  window.location.href = "/volatility-message"
                })
              }
            }} />
          </div>
        </div>
        <div className="flex-col gap-2">
          <div className="flex-col gap-4">
            <div className="white border border-gray-300 rounded p-4 bg-white">
              <p>
                <span className="font-semibold">발송 일시 : </span>
                {message.issuedAt}
              </p>
            </div>
            {/* 종전에는 상세 화면에 실패 사실조차 표시되지 않아, 통계가 모두 0 으로만 보였다.
                아래 지표는 발송이 된 건 기준이라 실패 건에서는 의미가 없으므로 맨 위에서 먼저 알린다. */}
            {message.status === "FAILED" && (
                <div className="border border-red-300 rounded p-4 bg-red-50 text-red-700">
                  <p className="font-semibold">발송에 실패했습니다.</p>
                  <p className="mt-2 whitespace-pre-wrap break-words">
                    <span className="font-semibold">실패 사유 : </span>
                    {message.errorText || "실패 사유가 기록되지 않았습니다. 고객센터로 문의해 주세요."}
                  </p>
                  <p className="mt-2 text-sm">
                    이 메시지는 발송되지 않았으므로 아래 발송·클릭·매출 지표는 집계되지 않습니다.
                    내용을 수정해 다시 보내려면 우측 상단 <span className="font-semibold">복제하기</span>를 사용하세요.
                  </p>
                </div>
            )}
            <div className="flex-col bg-white">
              <Panel items={[
                {
                  title: (
                      <>
                        <div className='panel-icon'>
                          <UsersIcon />
                        </div>
                        <p>매출액</p>
                      </>
                  ),
                  value: orderError ? "-" : orderTotalSales?.toLocaleString(),
                  suffix: orderError ? "집계 불가" : `원 (${orderTotalCount}건)`,
                },
                {
                  title: (
                      <>
                        <div className='panel-icon'>
                          <MouseIcon />
                        </div>
                        <p>클릭 고객 수 (추정) / 총 클릭 수</p>
                      </>
                  ),
                  value: `약 ${clickUserCount.toLocaleString()}명
                  (${message.totalSuccessSendCount > 0 ? Math.round(clickUserCount / message.totalSuccessSendCount * 100) : 0}%)`,
                  suffix: `/ ${message.totalClickCount?.toLocaleString()}번(${message.totalClickRate?.toLocaleString()}%)`,
                },
                {
                  title: (
                      <>
                        <div className='panel-icon'>
                          <CouponIcon />
                        </div>
                        <p>ROAS</p>
                      </>
                  ),
                  // ROAS 의 분자가 온라인 매출이다. 그 값을 못 받았으면 0% 가 아니라 '집계 불가' 다.
                  value: orderError ? "-" : (message.totalPrice > 0 ? Math.round(orderTotalSales / message.totalPrice * 100) : 0).toLocaleString(),
                  suffix: orderError ? "집계 불가" : `% (구매 전환율: ${(clickUserData?.totalClickUsers?.length ?? 0) > 0 ? Math.round(orderTotalCount / clickUserData.totalClickUsers.length * 100) : 0}%)`,
                },
                {
                  title: (
                      <>
                        <div className='panel-icon'>
                          <CoinIcon />
                        </div>
                        <p>발송 비용</p>
                      </>
                  ),
                  value: message.totalPrice?.toLocaleString() + '원',
                  suffix: `(${message.totalSuccessSendCount?.toLocaleString()}명)`,
                }
              ]} />
            </div>
            <Tab items={tabHeaders}
                 onChanged={(item) => {
                    setTab(item.value)
                  }}
            />
          </div>
          {
            tab === "init" && (
                <>
                  <InputGroup label="AI 메시지 분석">
                    <div className="whitespace-pre-line">
                      <Markdown>
                        {message.aiMessageConversionList?.[0]?.data?.replace(/<br\s*\/?>/gi, "\n") ?? "AI 분석은 전송 후 24시간이 지난 후 확인할 수 있습니다."}
                      </Markdown>
                    </div>
                  </InputGroup>
                  <InputGroup label="발송 조건" description="아래 조건을 포함하고 있는 대상에게 발송되었습니다.">
                    {filterCategories.length > 0 ?
                        filterCategories.map((label, i) => (<Chip key={`${label}-${i}`}>{label}</Chip>))
                        : (<Chip>전체</Chip>)}
                  </InputGroup>
                </>
              )
          }
          {
            tab === "ai" && (
                  <InputGroup label="AI 메시지 분석 챗봇">
                    <MiniAIChatForm histories={message.aiMessageConversionList
                        .map((item) => {
                          return (
                              {
                                type: item.type,
                                message: item.data
                              }
                          )
                        })} id={id}
                    />
                  </InputGroup>
              )
          }
          {
            tab === "engage" && (
                <>
                  <InputGroup label="발송 종류">
                    <Table headers={sentHeaders} data={[
                      [
                        <div>회원 고객</div>,
                        <div>{message.sendCount?.toLocaleString()}</div>,
                        <div>{message.successCount?.toLocaleString()}</div>,
                        <div>{message.clickCount?.toLocaleString()}</div>
                      ],
                      [
                        <div>회원이 아닌 친구</div>,
                        <div>{message.momentSendCount?.toLocaleString()}</div>,
                        <div>{message.momentSuccessSendCount?.toLocaleString()}</div>,
                        <div>{message.momentClickCount?.toLocaleString()}</div>
                      ]
                    ]}
                    />
                  </InputGroup>
                  <InputGroup label="발송 메시지 및 발송 분석">
                    <div className="flex">
                      <div className="flex-1 flex-col gap-4 pr-[12px]">
                        <Panel variants="bar" items={[
                          {title: "총 클릭 수", value: `${message.totalClickCount?.toLocaleString()}번`, valueStyle: {flex: "1"}}
                        ]} />
                        <div>
                          <Table headers={contentsClickHeaders} data={contentsClickUserTable} />
                        </div>
                      </div>
                      <div className="pl-[12px] flex-1 flex-center-all bg-depth1">
                        <MessageWrapPreview messageData={previewData} type={previewData.type} />
                      </div>
                    </div>
                  </InputGroup>
                  <InputGroup
                      label="혜택 성과"
                      description="해당 프로젝트에 참여한 고객 분석입니다."
                  >
                    <div className="flex-col gap-4">
                      {
                        message.benefitStatistics?.map(benefit => {
                          // 응모 완료형은 사용 단계 자체가 없다(BenefitType.APPLY_DONE) → '발급' 만 표시.
                          // 링크 혜택형은 2026-11-04 부터 '혜택 확인하기' 첫 클릭이 사용으로 기록된다 → '클릭/발급' 라벨.
                          const usedTrackable = benefit.isUsedTrackable !== false
                          const untrackableLabel = benefit.typeKor === "응모 완료형" ? "사용 단계 없음" : "사용 확인 불가"
                          const usedLabel = benefit.typeKor === "링크 혜택형" ? "클릭/발급(클릭률)" : "사용/발급(사용률)"
                          return (
                              <div className="flex-col gap-4">
                                <div className="flex justify-between" style={{paddingRight: "20px"}}>
                                  <div className="flex items-center gap-2">
                                    {benefit.typeKor && <div className="text-primary">[{benefit.typeKor}]</div>}
                                    <div className="font-bold">{benefit.name}</div>
                                  </div>
                                </div>
                                <div className="flex justify-between border">
                                  <div className="flex-align-center w-full justify-between" style={{padding: "10px 10px", borderRight: "1px solid var(--grey-1)"}}>
                                    <div>{usedTrackable ? usedLabel : "발급"}</div>
                                    {
                                      usedTrackable ? (
                                          <div className="flex gap-1 font-bold">
                                            <div className="text-primary">{benefit.usedCount?.toLocaleString()}</div>
                                            <div>/</div>
                                            <div>{benefit.totalCount?.toLocaleString()}</div>
                                            <div>({Math.round(benefit.usedRate)}%)</div>
                                          </div>
                                      ) : (
                                          <div className="flex items-center gap-2">
                                            <div className="font-bold">{benefit.totalCount?.toLocaleString()}</div>
                                            <div className="text-tertiary text-[12px]">{untrackableLabel}</div>
                                          </div>
                                      )
                                    }
                                  </div>
                                </div>
                                {
                                  usedTrackable ? (
                                      <EChartsReact
                                          option={basicBarOption([benefit.name], [
                                            {
                                              value: Math.round(benefit.usedRate),
                                              extraInfo: ` (${benefit.usedCount} / ${benefit.totalCount})`
                                            }
                                          ], "%")}
                                      />
                                  ) : (
                                      <div className="bg-depth1 text-tertiary text-[13px]" style={{padding: "12px"}}>
                                        링크 혜택형은 고객이 외부 링크로 이동하므로 <b>실제 사용 여부를 확인할 수 없습니다</b>.
                                        발급 수와 클릭 수로만 성과를 판단해 주세요.
                                      </div>
                                  )
                                }
                              </div>
                          )
                        })
                      }
                    </div>
                  </InputGroup>
                  <InputGroup label="메시지 클릭 고객 분석" description="메시지에 클릭한 고객을 분석합니다">
                    <div className="flex-col gap-2">
                      <div className="flex justify-between items-center border">
                        <div className="flex gap-2 p-20px">
                          <div>클릭 고객 수</div>
                          <div>/</div>
                          <div>메시지 발송 수</div>
                        </div>
                        <div className="flex-align-center gap-2" style={{paddingRight: "20px"}}>
                          <div style={{fontSize: "22px", fontWeight: "700"}}>
                            {clickUserData?.totalClickUsers?.length?.toLocaleString()}명
                            ({message.totalSuccessSendCount > 0 ? Math.round((clickUserData?.totalClickUsers?.length ?? 0) / message.totalSuccessSendCount * 1000) / 10 : 0}%)
                          </div>
                          <div style={{fontSize: "16px"}}>/</div>
                          <div style={{fontSize: "16px"}}>{message.totalSuccessSendCount?.toLocaleString()}명</div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="float-right">
                        {
                          message.issuedAt ? (
                              <CommonCalendarSearchBar periodStartDate={moment(message.issuedAt)}
                                                       periodEndDate={moment(message.issuedAt).add(7, "day")}
                                                       onConfirm={(dateRange) => {
                                                         setDateRange(dateRange)
                                                       }} />
                          ) : <></>
                        }
                      </div>
                      <EChartsReact
                          option={basicBarOption(clickUserChart.xAxis, clickUserChart.series)}
                      />
                    </div>
                  </InputGroup>
                  <InputGroup label={
                    <div className="flex gap-2">
                      <div>메시지 클릭 고객</div>
                      <div className="text-primary">{clickUserData?.totalClickUsers?.length?.toLocaleString()}명</div>
                    </div>
                  }
                  >
                    <Table headers={clickUserHeaders} data={totalClickUserTable}
                           pagination={{pageSize: 5}}
                    />
                  </InputGroup>
                </>
              )
          }
          {
            tab === "sales" && (
                <>
                  <InputGroup label={
                    <div className="flex gap-2">
                      <div>발송 후 24시간 이내 온라인 구매 고객</div>
                      <div className="text-primary">{orderError ? "-" : orderTotalCount.toLocaleString()}명</div>/
                      <span className="text-primary">{orderError ? "-" : orderTotalSales.toLocaleString()}원</span>
                    </div>
                  }>
                    {
                      orderError && (
                          <div className="w-full flex flex-col items-center justify-center gap-2 py-10">
                            <div className="text-sm font-semibold text-gray-700">온라인 주문 정보를 지금은 불러올 수 없습니다.</div>
                            <div className="text-xs text-gray-500 text-center px-4 break-words">{orderError}</div>
                            <div className="text-xs text-gray-400">잠시 후 다시 시도해 주세요.</div>
                          </div>
                      )
                    }
                    <Table headers={orderHeaders} data={orderTables} />
                    <div className="flex justify-center mt-[24px]">
                      <Pagination pageSize={orderLimit} totalCount={orderTotalCount} currentPage={orderPage}
                                  onPaging={(page) => setOrderPage(page)} />
                    </div>
                  </InputGroup>
                </>
              )
          }
          {
              tab === "sales_marketing" && (
                  <>
                    <InputGroup label={
                      <div className="flex gap-2">
                        <div>리마인드 필요 고객</div>
                        <div className="text-primary">{clickRemarketingUsers?.length?.toLocaleString()}명</div>
                      </div>
                    }
                    >
                      <div className="flex">
                        <div className="flex-1 flex-col gap-4 pr-[12px]">
                          <Table headers={clickUserHeaders} data={clickRemarketingUsers}
                                 pagination={{pageSize: 5}}
                          />
                        </div>
                        <div className="pl-[12px] flex-1 flex-center-all flex-col gap-2 bg-depth1">
                          <div className="font-semibold text-[24px]">AI 메시지 추천</div>
                          <MessageWrapPreview messageData={message.recommendMessage} type={message.recommendMessage?.type} />
                        </div>
                      </div>
                    </InputGroup>
                  </>
              )
          }
        </div>
      </div>
  );
};
export default VolatilityMessageDetail;
